/**
 * @module types/api
 * @description API-related type definitions for Zotero API interactions
 */

import type { Collection, Item, LibraryType } from './models';

// =============================================================================
// API KEY RESPONSE
// =============================================================================

/** Response from /keys/current endpoint */
export interface ApiKeyResponse {
  /** API key identifier */
  key: string;
  /** User ID associated with this key */
  userID: number;
  /** Username */
  username: string;
  /** Display name */
  displayName: string;
  /** Key access permissions */
  access: ApiKeyAccess;
}

/** API key access permissions */
export interface ApiKeyAccess {
  /** User library permissions */
  user?: {
    library?: boolean;
    files?: boolean;
    notes?: boolean;
    write?: boolean;
  };
  /** Group permissions (keyed by group ID) */
  groups?: {
    all?: {
      library?: boolean;
      write?: boolean;
    };
    [groupId: string]:
      | {
          library?: boolean;
          write?: boolean;
        }
      | undefined;
  };
}

// =============================================================================
// GROUP RESPONSE
// =============================================================================

/** Group information from API */
export interface ZoteroGroup {
  /** Group ID */
  id: number;
  /** Version */
  version: number;
  /** Group data */
  data: ZoteroGroupData;
}

/** Group data */
export interface ZoteroGroupData {
  /** Group ID */
  id: number;
  /** Version */
  version: number;
  /** Group name */
  name: string;
  /** Group description */
  description: string;
  /** Owner user ID */
  owner: number;
  /** Group type */
  type: 'PublicOpen' | 'PublicClosed' | 'Private';
  /** Library editing permissions */
  libraryEditing: 'members' | 'admins';
  /** Library reading permissions */
  libraryReading: 'members' | 'all';
  /** File editing permissions */
  fileEditing: 'members' | 'admins' | 'none';
  /** Number of members */
  members?: number;
}

// =============================================================================
// API REQUEST OPTIONS
// =============================================================================

/** Options for API requests */
export interface ApiRequestOptions {
  /** HTTP method */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** API key override */
  apiKey?: string;
  /** Request body */
  body?: unknown;
  /** Query parameters */
  params?: Record<string, string | number>;
  /** Version header for conflict detection */
  version?: number;
  /** Request timeout in ms */
  timeout?: number;
}

/** Options for fetching items */
export interface GetItemsOptions {
  /** Number of items to fetch */
  limit?: number;
  /** Starting offset */
  start?: number;
  /** Sort field */
  sort?: string;
  /** Sort direction */
  direction?: 'asc' | 'desc';
  /** Collection key to filter by */
  collectionKey?: string;
  /** Search query */
  q?: string;
  /** Search mode */
  qmode?: string;
  /** Item type filter */
  itemType?: string;
  /** AbortSignal for cancelling requests */
  signal?: AbortSignal;
}

// =============================================================================
// API RESPONSES
// =============================================================================

/** Generic API response wrapper */
export interface ApiResponse<T> {
  /** Response data */
  data: T;
  /** Response headers */
  headers?: Record<string, string>;
  /** Total results (for paginated responses) */
  totalResults?: number;
}

/** Batch create/update response */
export interface BatchResponse {
  /** Successfully created/updated items */
  successful: Record<string, Item>;
  /** Unchanged items */
  unchanged: Record<string, Item>;
  /** Failed items with error messages */
  failed: Record<string, { code: number; message: string }>;
}

// =============================================================================
// API ERRORS
// =============================================================================

/** Custom error class for Zotero API errors */
export interface ZoteroApiError extends Error {
  /** Error name */
  name: 'ZoteroError';
  /** HTTP status code */
  status?: number;
  /** Response object */
  response?: Response;
  /** Error code from API */
  code?: string;
}

/** API error response body */
export interface ApiErrorResponse {
  /** Error message */
  message?: string;
  /** Error code */
  code?: string;
  /** Additional error details */
  details?: Record<string, unknown>;
}

// =============================================================================
// API ENDPOINTS
// =============================================================================

/** Library endpoint prefix builder */
export type LibraryEndpoint = `/users/${number}` | `/groups/${number}`;

/** Build library endpoint */
export function buildLibraryEndpoint(libraryId: number, libraryType: LibraryType): LibraryEndpoint {
  return libraryType === 'user' ? `/users/${libraryId}` : `/groups/${libraryId}`;
}

// =============================================================================
// SERVICE RESULT TYPES
// =============================================================================

/** Result type for service operations */
export type ServiceResult<T> =
  | { success: true; data: T }
  | { success: false; error: string; code?: string };

/** Result for item actions */
export interface ActionResult {
  /** Whether the action succeeded */
  success: boolean;
  /** User-facing message */
  message: string;
  /** Message type for notifications */
  type?: 'success' | 'error' | 'warning' | 'info';
  /** Whether the action was cancelled by user */
  cancelled?: boolean;
}
